import { useCallback, useEffect, useRef, useState } from 'react'
import { loadCrmDataset } from '../data/googleSheets'
import { mergeConfirmedProspect as mergeProspectIntoDataset, normalizeName } from '../data/transform'
import type { ConfirmedProspect, CrmDataset } from '../types/crm'

export type CrmDataState = {
  data: CrmDataset | null
  status: 'loading' | 'ready' | 'error'
  error: string | null
  mergeConfirmedProspect: (prospect: ConfirmedProspect) => void
  refresh: (options?: RefreshCrmDataOptions) => void
  refreshing: boolean
  lastRefreshedAt: number | null
}

export type RefreshCrmDataOptions = {
  cacheBust?: boolean
}

export function useCrmData(): CrmDataState {
  const [data, setData] = useState<CrmDataset | null>(null)
  const [status, setStatus] = useState<CrmDataState['status']>('loading')
  const [error, setError] = useState<string | null>(null)
  const [loadRequest, setLoadRequest] = useState({ cacheBust: false, key: 0 })
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null)
  const pendingConfirmedRef = useRef<ConfirmedProspect[]>([])

  const refresh = useCallback((options: RefreshCrmDataOptions = {}) => {
    setRefreshing(true)
    setLoadRequest((current) => ({
      cacheBust: options.cacheBust ?? true,
      key: current.key + 1,
    }))
  }, [])

  const mergeConfirmedProspect = useCallback((prospect: ConfirmedProspect) => {
    pendingConfirmedRef.current = [
      ...pendingConfirmedRef.current.filter((pending) => (
        pending.opportunity.id !== prospect.opportunity.id
        && pending.opportunity.sheetRow !== prospect.opportunity.sheetRow
      )),
      prospect,
    ]
    setData((current) => current ? mergeProspectIntoDataset(current, prospect) : current)
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    if (!data) setStatus('loading')
    setError(null)

    const load = loadRequest.cacheBust
      ? loadCrmDataset(controller.signal, { cacheBust: true })
      : loadCrmDataset(controller.signal)

    load
      .then((dataset) => {
        let reconciled = dataset
        const stillPending: ConfirmedProspect[] = []

        for (const confirmed of pendingConfirmedRef.current) {
          const organizationPresent = dataset.organizations.some((organization) => (
            organization.id === confirmed.organization.id
            || organization.sheetRow === confirmed.organization.sheetRow
            || normalizeName(organization.name) === normalizeName(confirmed.organization.name)
          ))
          const opportunityPresent = dataset.opportunities.some((opportunity) => (
            opportunity.id === confirmed.opportunity.id
            || opportunity.sheetRow === confirmed.opportunity.sheetRow
            || (
              normalizeName(opportunity.name) === normalizeName(confirmed.opportunity.name)
              && normalizeName(opportunity.organization) === normalizeName(confirmed.opportunity.organization)
            )
          ))

          if (!organizationPresent || !opportunityPresent) {
            reconciled = mergeProspectIntoDataset(reconciled, confirmed)
            stillPending.push(confirmed)
          }
        }

        pendingConfirmedRef.current = stillPending
        setData(reconciled)
        setStatus('ready')
        setLastRefreshedAt(Date.now())
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : 'The Google Sheet could not be loaded.')
        setStatus('error')
      })
      .finally(() => {
        if (!controller.signal.aborted) setRefreshing(false)
      })

    return () => controller.abort()
  }, [loadRequest])

  return { data, status, error, mergeConfirmedProspect, refresh, refreshing, lastRefreshedAt }
}

